import { Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

export function SplashPage() {
  const { isSignedIn, isLoaded } = useAuth();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
      {/* Header */}
      <header className="px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-600 rounded-lg flex items-center justify-center shadow-lg shadow-green-500/25">
            <span className="text-white text-xl">✓</span>
          </div>
          <span className="text-xl font-bold text-white">
            Demo<span className="text-green-400">Path</span>
          </span>
        </div>

        <nav className="flex items-center gap-4">
          {!isLoaded ? (
            <div className="animate-pulse w-24 h-10 bg-slate-700 rounded-lg" />
          ) : isSignedIn ? (
            <Link
              to="/app"
              className="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg hover:shadow-lg hover:shadow-green-500/30 transition-all"
            >
              Open App
            </Link>
          ) : (
            <>
              <Link
                to="/sign-in"
                className="px-5 py-2.5 text-sm font-medium text-slate-300 hover:text-white transition-colors"
              >
                Log In
              </Link>
              <Link
                to="/sign-up"
                className="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg hover:shadow-lg hover:shadow-green-500/30 transition-all"
              >
                Sign Up Free
              </Link>
            </>
          )}
        </nav>
      </header>

      {/* Main Content */}
      <div className="flex-grow flex items-center justify-center px-6 py-12">
        <div className="max-w-4xl mx-auto text-center">
          {/* Hero Text */}
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight">
            Never Miss a <span className="text-green-400">Talking Point</span>
          </h1>

          <p className="text-xl md:text-2xl text-slate-300 mb-4 max-w-2xl mx-auto">
            Real-time speech recognition that tracks your demo coverage.
          </p>

          <p className="text-lg text-slate-400 mb-12 max-w-xl mx-auto">
            Paste your agenda, start talking, and watch items fade as you cover them.
            Stay on track without breaking your flow.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            {isSignedIn ? (
              <Link
                to="/app"
                className="group inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl shadow-lg shadow-green-500/30 hover:shadow-green-500/50 hover:scale-105 transition-all duration-200"
              >
                Go to App
                <svg
                  className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
            ) : (
              <>
                <Link
                  to="/sign-up"
                  className="group inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl shadow-lg shadow-green-500/30 hover:shadow-green-500/50 hover:scale-105 transition-all duration-200"
                >
                  Get Started Free
                  <svg
                    className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
                <Link
                  to="/sign-in"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-slate-300 border border-slate-600 rounded-xl hover:bg-slate-800 hover:border-slate-500 transition-all"
                >
                  I have an account
                </Link>
              </>
            )}
          </div>

          {/* Feature Cards */}
          <div className="grid md:grid-cols-3 gap-6">
            <FeatureCard
              icon="🎯"
              title="Paste & Go"
              description="Drop in your talking points. One per line. We handle the rest."
            />
            <FeatureCard
              icon="🎙️"
              title="Real-time Tracking"
              description="AI listens as you speak and checks off items automatically."
            />
            <FeatureCard
              icon="👀"
              title="Glanceable UI"
              description="Draggable widget stays in your peripheral vision during screenshares."
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-6 px-6 border-t border-slate-700/50">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            From the team at{' '}
            <a
              href="https://apparently.dev"
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-400 hover:text-green-300 transition-colors"
            >
              apparently.dev
            </a>
          </p>
          <div className="flex items-center gap-6 text-sm">
            <Link
              to="/privacy"
              className="text-slate-500 hover:text-slate-300 transition-colors"
            >
              Privacy Policy
            </Link>
            <a
              href="mailto:support@demopath.ai"
              className="text-slate-500 hover:text-slate-300 transition-colors"
            >
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, description }: { icon: string; title: string; description: string }) {
  return (
    <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 hover:border-green-500/30 transition-colors">
      <div className="text-3xl mb-3">{icon}</div>
      <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
      <p className="text-slate-400 text-sm">{description}</p>
    </div>
  );
}
